import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useRef } from "react";
import { useReactFlow, useStore } from "@xyflow/react";
const ACTIVE_FILL = "#3B82F6";
const ACTIVE_STROKE = "#1D4ED8";
const GHOST_STROKE = "#60A5FA";
/**
 * Single draggable control point rendered inside the RF edge-label layer.
 *
 * Interaction model (mirrors DiagramX / xyflow discussion #1029):
 *  - pointerdown: stopPropagation (keep RF from panning), capture pointer,
 *    attach pointermove/up to the RF wrapper (not `window`) so the drag stays
 *    scoped to the canvas element.
 *  - pointermove: translate clientX/Y through `screenToFlowPosition` (required
 *    for correct behavior under zoom/pan — raw client coords break both).
 *  - pointerup: fires once via `{ once: true }`, detaches the move listener,
 *    then calls `onDragEnd` so persistence / undo happens at drop time only.
 *  - contextmenu (active only): calls `onDelete`, preventDefault to suppress
 *    the browser's native menu.
 */ function ControlPointInner({ id, x, y, active, onDrag, onDragEnd, onDragStart, onDelete, onMouseEnter, onMouseLeave }) {
    const rf = useReactFlow();
    // RF's wrapper DOM node, the preferred listener target per xyflow patterns:
    // window-scoped listeners can outlive the canvas or race re-mounts.
    const domNode = useStore((s)=>s.domNode);
    const draggingRef = useRef(false);
    const handlePointerDown = useCallback((e)=>{
        if (e.button !== 0) return; // left button only
        e.stopPropagation();
        e.preventDefault();
        e.target.setPointerCapture?.(e.pointerId);
        draggingRef.current = true;
        onDragStart?.();
        const target = domNode ?? window;
        const handleMove = (ev)=>{
            if (!draggingRef.current) return;
            const flow = rf.screenToFlowPosition({
                x: ev.clientX,
                y: ev.clientY
            });
            onDrag(flow.x, flow.y);
        };
        const handleUp = ()=>{
            if (!draggingRef.current) return;
            draggingRef.current = false;
            target.removeEventListener("pointermove", handleMove);
            onDragEnd();
        };
        target.addEventListener("pointermove", handleMove);
        target.addEventListener("pointerup", handleUp, {
            once: true
        });
    }, [
        domNode,
        rf,
        onDrag,
        onDragEnd,
        onDragStart
    ]);
    const handleContextMenu = useCallback((e)=>{
        if (!active || !onDelete) return;
        e.preventDefault();
        e.stopPropagation();
        onDelete();
    }, [
        active,
        onDelete
    ]);
    const size = active ? 10 : 8;
    return /*#__PURE__*/ _jsx("div", {
        "data-edge-control-point": true,
        "data-control-point-id": id,
        className: "nodrag nopan",
        onPointerDown: handlePointerDown,
        onContextMenu: handleContextMenu,
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        style: {
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
            width: size,
            height: size,
            borderRadius: "50%",
            background: active ? ACTIVE_FILL : "transparent",
            border: active ? `1px solid ${ACTIVE_STROKE}` : `1px dashed ${GHOST_STROKE}`,
            opacity: active ? 1 : 0.6,
            cursor: active ? "grab" : "copy",
            pointerEvents: "all",
            touchAction: "none",
            zIndex: 1002
        }
    });
}
export const ControlPoint = /*#__PURE__*/ memo(ControlPointInner);

//# sourceMappingURL=ControlPoint.js.map